import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Wrench } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface CheckedInTrailer {
  id: string;
  trailer_id: string;
  trailer_type_id: string | null;
  load_status: string | null;
  needs_service: boolean | null;
  needs_service_reason: string | null;
  current_location: { short_code: string } | null;
}

interface CheckedInTrailerComboboxProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
}

export function CheckedInTrailerCombobox({
  value,
  onChange,
  placeholder = "e.g., TRL-001",
  id,
  disabled = false,
}: CheckedInTrailerComboboxProps) {
  const { tenantId } = useTenant();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data: trailers = [], isLoading } = useQuery({
    queryKey: ["checked-in-trailers", tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trailers")
        .select("id, trailer_id, trailer_type_id, load_status, needs_service, needs_service_reason, current_location:locations!trailers_current_location_id_fkey(short_code)")
        .eq("tenant_id", tenantId!)
        .eq("current_location_status", "at_site")
        .not("current_location_id", "is", null)
        .order("trailer_id");
      if (error) throw error;
      return (data ?? []) as unknown as CheckedInTrailer[];
    },
    enabled: !!tenantId,
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return trailers;
    return trailers.filter((t) => t.trailer_id.toLowerCase().includes(q));
  }, [trailers, search]);

  const trimmedSearch = search.trim();
  const hasExactMatch = trailers.some(
    (t) => t.trailer_id.toLowerCase() === trimmedSearch.toLowerCase(),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
          )}
        >
          {value || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search checked-in trailers..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[260px] overflow-y-auto">
            {filtered.length === 0 && !trimmedSearch && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {isLoading ? "Loading..." : "No checked-in trailers found."}
              </div>
            )}
            <CommandGroup>
              <CommandItem
                value="__clear__"
                onSelect={() => {
                  onChange("");
                  setSearch("");
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    !value ? "opacity-100" : "opacity-0",
                  )}
                />
                None
              </CommandItem>
            </CommandGroup>
            {filtered.length > 0 && (
              <CommandGroup heading={`Checked-in trailers (${trailers.length})`}>
                {filtered.map((trailer) => (
                <CommandItem
                  key={trailer.id}
                  value={trailer.trailer_id}
                  onSelect={(selected) => {
                    onChange(selected);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === trailer.trailer_id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="font-medium flex items-center gap-1.5">
                      {trailer.trailer_id}
                      {trailer.needs_service && (
                        <span className="inline-flex items-center gap-1 rounded-sm bg-destructive/10 text-destructive px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide">
                          <Wrench className="h-3 w-3" />
                          Needs Service
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground truncate">
                      {[
                        trailer.current_location?.short_code,
                        trailer.load_status && trailer.load_status !== "unknown"
                          ? trailer.load_status.charAt(0).toUpperCase() + trailer.load_status.slice(1).toLowerCase()
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </span>
                  </div>
                </CommandItem>
                ))}
              </CommandGroup>
            )}
            {trimmedSearch && !hasExactMatch && (
              <CommandGroup>
                <CommandItem
                  value={`__use__${trimmedSearch}`}
                  onSelect={() => {
                    onChange(trimmedSearch);
                    setOpen(false);
                  }}
                >
                  <span className="text-sm">
                    Use <span className="font-medium">"{trimmedSearch}"</span>
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}